import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, Users, Globe, BookOpen, Brain, Sparkles,
  Lock, Crown, GraduationCap, MapPin, Scale, History,
  Briefcase, Building2, ShoppingCart, Check
} from "lucide-react";
import OptionalLockedPage from "@/components/OptionalLockedPage";
import axios from "axios";

import { API_BASE } from "@/services/api";

/**
 * Optional Hub Page
 * Entry point for all Optional Subject Suites
 * Shows available optionals with subject-specific cards
 */
const OptionalHubPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Track purchased optionals
  const [purchasedOptionals, setPurchasedOptionals] = useState([]);
  const [selectedLocked, setSelectedLocked] = useState(null);

  // Optional pricing - Founding rates ₹1,999 (anchor ₹5,999)
  const optionalPrices = {
    anthropology: { 
      price: 1999, 
      display: "₹1,999",
      original: 5999,
      originalDisplay: "₹5,999"
    },
    sociology: { 
      price: 1999, 
      display: "₹1,999",
      original: 5999,
      originalDisplay: "₹5,999"
    },
    "pub-admin": { 
      price: 1999, 
      display: "₹1,999",
      original: 5999,
      originalDisplay: "₹5,999"
    },
    psir: { 
      price: 1999, 
      display: "₹1,999",
      original: 5999,
      originalDisplay: "₹5,999"
    }
  };

  useEffect(() => {
    // Get user's purchased optionals
    if (user?.purchased_optionals) {
      setPurchasedOptionals(user.purchased_optionals);
    }
  }, [user]);

  const hasPurchased = (optionalId) => {
    // Map the frontend id to backend key
    const keyMap = {
      "anthropology": "anthropology",
      "sociology": "sociology", 
      "pub-admin": "pubadmin",
      "psir": "psir"
    };
    return purchasedOptionals.includes(keyMap[optionalId] || optionalId);
  };

  const handleEnterSuite = (subject) => {
    if (!subject.available) return;
    
    if (hasPurchased(subject.id)) {
      // User has purchased - go directly to suite
      navigate(subject.path);
    } else {
      // Show locked/payment page
      const keyMap = {
        "anthropology": "anthropology",
        "sociology": "sociology", 
        "pub-admin": "pubadmin",
        "psir": "psir"
      };
      const pricing = optionalPrices[subject.id] || { price: 1999, display: "₹1,999" };
      
      setSelectedLocked({
        locked: true,
        optional_key: keyMap[subject.id] || subject.id,
        optional_name: subject.name,
        price: pricing.price,
        price_display: pricing.display,
        original_price: pricing.original,
        original_price_display: pricing.originalDisplay,
        message: `🔒 ${subject.name} Optional is a premium feature`,
        description: `Get complete access to ${subject.name} Paper 1 & Paper 2 with all features:`,
        features: [
          "✅ AI-powered Answer Writing (ENGINE-1)",
          "✅ Answer Evaluation by Examiner AI (ENGINE-2)",
          "✅ 30-Year PYQ Analysis (ENGINE-3)",
          "✅ Expected Questions (ENGINE-4)",
          "✅ Intelligent Diagram Suggestions",
          "✅ Topper-level answer frameworks",
          "✅ Unlimited generations"
        ],
        cta_text: `Buy ${subject.name} Optional`
      });
    }
  };

  // If showing locked page, render it
  if (selectedLocked) {
    return (
      <OptionalLockedPage 
        lockedData={selectedLocked}
        onBack={() => setSelectedLocked(null)}
      />
    );
  }

  const optionalSubjects = [
    {
      id: "anthropology",
      name: "Anthropology",
      tagline: "Physical + Social Anthropology",
      description: "Paper I & Paper II Complete Intelligence Suite",
      icon: Users,
      color: "from-amber-500 to-orange-500",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      path: "/optional/anthropology",
      available: true,
      papers: ["Paper I - Physical & Social", "Paper II - Indian Anthropology"],
      features: ["PYQ Intelligence", "Answer Workspace", "Diagram Library", "Evaluation Engine"]
    },
    {
      id: "geography",
      name: "Geography",
      tagline: "Physical + Human Geography",
      description: "Comprehensive Geography Optional Suite",
      icon: Globe,
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      path: "/optional/geography",
      available: false,
      papers: ["Paper I - Physical Geography", "Paper II - Human Geography"],
      features: ["Map Diagram Engine", "Model Answers", "PYQ Analysis"]
    },
    {
      id: "sociology",
      name: "Sociology",
      tagline: "Foundations + Indian Society",
      description: "Theory-focused Sociology Suite",
      icon: Building2,
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      path: "/optional/sociology",
      available: true,
      papers: ["Paper I - Fundamentals", "Paper II - Indian Society"],
      features: ["Thinker Database", "Answer Structures", "Evaluation"]
    },
    {
      id: "psir",
      name: "Political Science & IR",
      tagline: "Political Theory + International Relations",
      description: "PSIR Optional Intelligence Suite",
      icon: Scale,
      color: "from-blue-500 to-indigo-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      path: "/optional/psir",
      available: true,
      papers: ["Paper I - Political Theory", "Paper II - International Relations"],
      features: ["Current Affairs Integration", "Model Answers", "PYQ Trends", "Evaluation"]
    },
    {
      id: "history",
      name: "History",
      tagline: "Ancient + Medieval + Modern India",
      description: "History Optional Complete Suite",
      icon: History,
      color: "from-rose-500 to-pink-500",
      bgColor: "bg-rose-50",
      borderColor: "border-rose-200",
      path: "/optional/history",
      available: false,
      papers: ["Paper I - Indian History", "Paper II - World History"],
      features: ["Timeline Engine", "Map Questions", "Evaluation"]
    },
    {
      id: "pub-admin",
      name: "Public Administration",
      tagline: "Administrative Theory + Indian Administration",
      description: "Pub Ad Optional Intelligence Suite",
      icon: Briefcase,
      color: "from-slate-600 to-gray-700",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200",
      path: "/optional/pub-admin",
      available: true,
      papers: ["Paper I - Administrative Theory", "Paper II - Indian Administration"],
      features: ["4-Engine System", "Top Ranker Mode", "30-Year PYQ Analysis"]
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50/30 pb-24">
      {/* Premium Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-orange-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        
        {/* Hero Section */}
        <div className="rounded-2xl p-8 bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 text-white text-center shadow-xl">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-white/30 flex items-center justify-center shadow-lg">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3 text-white drop-shadow-md">
            Optional Subjects Hub
          </h1>
          <p className="text-white/90 max-w-2xl mx-auto mb-4 text-lg">
            Exam-rank optimisation systems for UPSC Optional Papers.
            <br />
            <span className="font-medium">Structure beats brilliance. Handling beats knowledge.</span>
          </p>
          <Badge className="px-4 py-2 text-sm bg-white/30 text-white border-0 font-medium shadow-md">
            {purchasedOptionals.length > 0 
              ? `✨ ${purchasedOptionals.length} Optional${purchasedOptionals.length > 1 ? 's' : ''} Purchased`
              : '📚 Choose Your Optional Subject'}
          </Badge>
        </div>

        {/* Available Subjects */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-slate-900">Choose Your Optional</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {optionalSubjects.map((subject) => {
              const Icon = subject.icon;
              const isPurchased = hasPurchased(subject.id);
              const pricing = optionalPrices[subject.id];
              
              return (
                <Card 
                  key={subject.id}
                  className={`overflow-hidden transition-all duration-300 ${
                    subject.available 
                      ? `hover:shadow-xl cursor-pointer ${subject.borderColor} border-2` 
                      : 'opacity-60 border-dashed border-2 border-slate-300'
                  }`}
                  onClick={() => subject.available && handleEnterSuite(subject)}
                >
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className={`p-5 bg-gradient-to-r ${subject.color} text-white`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                            <Icon className="w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg">{subject.name}</h3>
                            <p className="text-xs text-white/80">{subject.tagline}</p>
                          </div>
                        </div>
                        {!subject.available ? (
                          <Badge className="bg-white/20 text-white text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Coming Soon
                          </Badge>
                        ) : isPurchased ? (
                          <Badge className="bg-green-500/90 text-white text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Purchased
                          </Badge>
                        ) : pricing ? (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-white/60 line-through">{pricing.originalDisplay}</span>
                            <Badge className="bg-white/20 text-white text-xs font-semibold">
                              {pricing.display}
                            </Badge>
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Body */}
                    <div className={`p-5 ${subject.bgColor}`}>
                      <p className="text-sm text-slate-600 mb-4">{subject.description}</p>
                      
                      {/* Papers */}
                      <div className="space-y-2 mb-4">
                        {subject.papers.map((paper, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs">
                            <BookOpen className="w-3 h-3 text-slate-500" />
                            <span className="text-slate-700">{paper}</span>
                          </div>
                        ))}
                      </div>

                      {/* Features */}
                      <div className="flex flex-wrap gap-1 mb-4">
                        {subject.features.slice(0, 3).map((feature, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px]">
                            {feature}
                          </Badge>
                        ))}
                        {subject.features.length > 3 && (
                          <Badge variant="outline" className="text-[10px]">
                            +{subject.features.length - 3} more
                          </Badge>
                        )}
                      </div>

                      {/* Action */}
                      {subject.available ? (
                        isPurchased ? (
                          <Button 
                            className={`w-full bg-gradient-to-r ${subject.color} hover:opacity-90`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(subject.path);
                            }}
                          >
                            Enter Suite
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        ) : (
                          <Button 
                            className={`w-full bg-gradient-to-r ${subject.color} hover:opacity-90`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEnterSuite(subject);
                            }}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Buy Now - {pricing?.display || '₹1,999'}
                          </Button>
                        )
                      ) : (
                        <Button variant="outline" className="w-full" disabled>
                          <Lock className="w-4 h-4 mr-2" />
                          Coming Soon
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Philosophy Card */}
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5" />
              CLARIAS Optional Philosophy
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-semibold mb-2">🎯 Exam-Rank Optimisation</p>
                <p className="text-slate-300 text-xs">
                  Not a tutor. Not a notes compiler. An exam-rank optimisation system.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-semibold mb-2">📊 PYQ-Driven Intelligence</p>
                <p className="text-slate-300 text-xs">
                  Every response is PYQ-anchored with 30 years of trend analysis.
                </p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="font-semibold mb-2">✍️ Examiner-Aligned Evaluation</p>
                <p className="text-slate-300 text-xs">
                  Replicating UPSC marking logic with handling-based scoring.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OptionalHubPage;
